/**
 * Lightweight local API wrapper.
 *
 * Many UI screens import `apiWrapper` from `merchantApiClientWrapper`.
 * In the upstream project this wrapper is provided by `merchantdashboardclientwrapper`
 * (a private package). In this repo/environment the dependency cannot be resolved
 * at build-time, and we must avoid bundling Node-only modules like `fs`.
 *
 * This wrapper provides the same "shape" used across the UI via a Proxy and
 * forwards methods through `AxiosInstance` when possible.
 */

import apiInstance from "AxiosInstance";

/**
 * Exact method → controller path overrides.
 * getAllX used to strip "getAll" and hit plural paths (Suppliers, UserRoles)
 * which 404 against ASP.NET [controller] routes (Supplier, Role, …).
 */
const METHOD_PATH_OVERRIDES = {
  getAllSuppliers: "Supplier",
  getAllUserRoles: "Role",
  getAllUserSystemModules: "SystemModule",
  getAllUserPermissions: "Permission",
  createRole: "Role",
  getAllbrand: "Brand",
  getAllCategory: "Category",
  getAllattribute: "Attribute",
  getAllattributeType: "AttributeType",
  getAllProductCondition: "ProductCondition",
  getAllDeliverytime: "DeliveryTime",
};

/** Resource key → controller name when method itself has no path cue (create/delete/get). */
const RESOURCE_PATH_OVERRIDES = {
  supplier: "Supplier",
  user: "Users",
  brand: "Brand",
  category: "Category",
  attribute: "Attribute",
  attributeType: "AttributeType",
  ProductType: "ProductType",
  productCondition: "ProductCondition",
  deliverytime: "DeliveryTime",
  MasterProduct: "MasterProduct",
};

function getMethodSuffix(methodName) {
  const m = String(methodName || "");
  if (m.startsWith("getAll")) return m.slice("getAll".length);
  if (m.startsWith("getSingle")) return m.slice("getSingle".length);
  if (m.startsWith("delete")) return m.slice("delete".length);
  return "";
}

function pascalCase(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Drop a trailing plural "s" when the rest looks like a controller name (Suppliers → Supplier). */
function singularizeController(name) {
  const s = String(name || "");
  if (s.length > 3 && /[a-z]s$/i.test(s) && !/(ss|us|is|Status|News|Fonts)$/i.test(s)) {
    return s.slice(0, -1);
  }
  return s;
}

function inferResourceBase(resourceKey, methodName) {
  if (METHOD_PATH_OVERRIDES[methodName]) {
    return METHOD_PATH_OVERRIDES[methodName];
  }

  const suffix = getMethodSuffix(methodName);
  if (suffix) {
    // Prefer resource key when suffix is a plural form of it (supplier + Suppliers).
    const resourceHint = RESOURCE_PATH_OVERRIDES[resourceKey] || pascalCase(resourceKey);
    const fromSuffix = singularizeController(pascalCase(suffix));
    if (
      resourceHint &&
      fromSuffix.toLowerCase() !== resourceHint.toLowerCase() &&
      fromSuffix.toLowerCase().startsWith(resourceHint.toLowerCase())
    ) {
      return resourceHint;
    }
    return fromSuffix || resourceHint;
  }

  if (RESOURCE_PATH_OVERRIDES[resourceKey]) {
    return RESOURCE_PATH_OVERRIDES[resourceKey];
  }
  return pascalCase(resourceKey);
}

function apiMethod(resourceKey, methodName) {
  const basePath = inferResourceBase(resourceKey, methodName);

  if (methodName === "get" || methodName.startsWith("getSingle")) {
    return async (id) => apiInstance.get(`${basePath}/${id}`);
  }
  if (methodName === "getAll" || methodName.startsWith("getAll")) {
    return async () => apiInstance.get(basePath);
  }
  if (methodName === "create" || methodName === "createRole") {
    return async (payload) => apiInstance.post(basePath, payload);
  }
  if (methodName === "update") {
    return async (payload, id) => apiInstance.put(`${basePath}/${id}`, payload);
  }
  if (methodName === "put") {
    return async (id, payload) => apiInstance.put(`${basePath}/${id}`, payload);
  }
  if (methodName === "post") {
    return async (payload) => apiInstance.post(basePath, payload);
  }
  if (methodName === "delete" || methodName.startsWith("delete")) {
    return async (id) => apiInstance.delete(`${basePath}/${id}`);
  }
  if (methodName === "search") {
    return async (values) => apiInstance.post(`${basePath}/search`, values);
  }

  // Unknown method: safe fallback for build-time compilation.
  return async () => ({ data: [] });
}

const apiWrapper = new Proxy(
  { apiInstance },
  {
    get(_target, resourceKey) {
      if (resourceKey === "apiInstance") return apiInstance;
      if (resourceKey === "__esModule") return false;

      return new Proxy(
        {},
        {
          get(_inner, methodName) {
            if (typeof methodName !== "string") return undefined;
            return apiMethod(resourceKey, methodName);
          },
        }
      );
    },
  }
);

export default apiWrapper;
